/* Created by Interactive 63 - https://interactive63.com */
/**
 * How the editor looks: the dark or light theme, the accent colour and the colour of keyframes on
 * the timeline. The choices are applied as CSS custom properties on the document and remembered
 * between visits. Green is the default. Every accent is defined for both themes so text on it stays readable.
 */
import { readStorage, writeStorage } from './storage';

export type Theme = 'dark' | 'light';
export type AccentId = 'purple' | 'mustard' | 'rust' | 'green';
export type KeyframeColourId = 'auto' | 'gold' | 'violet' | 'cyan';
export type KeyframeHue = Exclude<KeyframeColourId, 'auto'>;

/** The colours an accent supplies for one theme. */
export interface AccentTokens {
  /** Fill for primary buttons and selected controls. */
  accent: string;
  /** Text and icons in the accent colour, drawn on the panel background. */
  accentText: string;
  /** Colour of the keyboard focus ring. */
  focus: string;
  /** Text drawn on top of the accent fill. */
  contrast: string;
  /** Quiet background for selected rows. */
  soft: string;
}

export const ACCENTS: Record<AccentId, { label: string; dark: AccentTokens; light: AccentTokens }> =
  {
    purple: {
      label: 'Purple',
      dark: {
        accent: '#6b4bf5',
        accentText: '#a58fff',
        focus: '#a58fff',
        contrast: '#ffffff',
        soft: '#2c2650',
      },
      light: {
        accent: '#5b3df5',
        accentText: '#5b3df5',
        focus: '#5b3df5',
        contrast: '#ffffff',
        soft: '#e5dfff',
      },
    },
    mustard: {
      label: 'Mustard',
      dark: {
        accent: '#f18f01',
        accentText: '#f5b03e',
        focus: '#f5b03e',
        contrast: '#16181d',
        soft: '#3b2a0a',
      },
      light: {
        accent: '#f18f01',
        accentText: '#8a5200',
        focus: '#8a5200',
        contrast: '#1b1e25',
        soft: '#fdebcc',
      },
    },
    rust: {
      label: 'Rust red',
      dark: {
        accent: '#b4371f',
        accentText: '#ff9a85',
        focus: '#ff9a85',
        contrast: '#ffffff',
        soft: '#3a1d17',
      },
      light: {
        accent: '#b4371f',
        accentText: '#b4371f',
        focus: '#b4371f',
        contrast: '#ffffff',
        soft: '#fbe3dc',
      },
    },
    green: {
      label: 'Deep green',
      dark: {
        accent: '#2f7d4f',
        accentText: '#7ad9a1',
        focus: '#7ad9a1',
        contrast: '#ffffff',
        soft: '#1c3327',
      },
      light: {
        accent: '#2f7d4f',
        accentText: '#2b7448',
        focus: '#2b7448',
        contrast: '#ffffff',
        soft: '#dcf0e3',
      },
    },
  };

export const KEYFRAME_HUES: Record<KeyframeHue, { label: string; dark: string; light: string }> = {
  gold: { label: 'Gold', dark: '#f0b429', light: '#b27300' },
  violet: { label: 'Violet', dark: '#a58fff', light: '#5b3df5' },
  cyan: { label: 'Cyan', dark: '#38bdf8', light: '#0369a1' },
};

export const DEFAULT_ACCENT: AccentId = 'green';
export const DEFAULT_KEYFRAME_COLOUR: KeyframeColourId = 'auto';

/** Fired on the window after the look changes, so canvases that cache nothing can redraw. */
export const APPEARANCE_EVENT = 'tweener:appearance';

const THEME_KEY = 'svg-tempo:theme';
const ACCENT_KEY = 'svg-tempo:accent';
const KEYFRAME_KEY = 'svg-tempo:keyframe-colour';

/** Whether a stored value names one of the accents. */
function isAccent(value: string | null): value is AccentId {
  return value !== null && value in ACCENTS;
}

/** Whether a stored value names a keyframe colour choice. */
function isKeyframeColour(value: string | null): value is KeyframeColourId {
  return value === 'auto' || (value !== null && value in KEYFRAME_HUES);
}

/**
 * The keyframe colour to draw. Automatic is gold, except on the mustard accent, where gold would
 * blend into the buttons and violet stands out instead.
 */
export function resolveKeyframeHue(choice: KeyframeColourId, accent: AccentId): KeyframeHue {
  if (choice !== 'auto') return choice;
  return accent === 'mustard' ? 'violet' : 'gold';
}

/** Returns the theme currently applied to the document. */
export function currentTheme(): Theme {
  return document.documentElement.dataset['theme'] === 'light' ? 'light' : 'dark';
}

/** Returns the accent the user has chosen. */
export function currentAccent(): AccentId {
  const stored = readStorage(ACCENT_KEY);
  return isAccent(stored) ? stored : DEFAULT_ACCENT;
}

/** Returns the keyframe colour choice, which may be automatic. */
export function currentKeyframeColour(): KeyframeColourId {
  const stored = readStorage(KEYFRAME_KEY);
  return isKeyframeColour(stored) ? stored : DEFAULT_KEYFRAME_COLOUR;
}

/** Writes the accent and keyframe custom properties for the current theme. */
function paint(): void {
  const theme = currentTheme();
  const accentId = currentAccent();
  const tokens = ACCENTS[accentId][theme];
  const hue = KEYFRAME_HUES[resolveKeyframeHue(currentKeyframeColour(), accentId)][theme];
  const style = document.documentElement.style;
  style.setProperty('--accent', tokens.accent);
  style.setProperty('--accent-text', tokens.accentText);
  style.setProperty('--focus', tokens.focus);
  style.setProperty('--accent-contrast', tokens.contrast);
  style.setProperty('--accent-soft', tokens.soft);
  style.setProperty('--keyframe', hue);
  window.dispatchEvent(new Event(APPEARANCE_EVENT));
}

/** Sets the theme on the document root and remembers the choice. */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset['theme'] = theme;
  writeStorage(THEME_KEY, theme);
  paint();
}

/** Sets the accent colour and remembers the choice. */
export function applyAccent(accent: AccentId): void {
  writeStorage(ACCENT_KEY, accent);
  paint();
}

/** Sets the keyframe colour and remembers the choice. */
export function applyKeyframeColour(choice: KeyframeColourId): void {
  writeStorage(KEYFRAME_KEY, choice);
  paint();
}

/** Applies the saved look, defaulting to the dark theme with the purple accent. */
export function restoreAppearance(): void {
  applyTheme(readStorage(THEME_KEY) === 'light' ? 'light' : 'dark');
}
